import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Input,
  message,
  Statistic,
  Row,
  Col,
  Tooltip,
} from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { permissionRequestsApi } from '@/api/permission-requests'
import type { PermissionRequest } from '@/types/permission'
import { getApiErrorMessage } from '@/utils/error'

dayjs.extend(relativeTime)

const { TextArea } = Input

export const ApprovalQueue = () => {
  const queryClient = useQueryClient()
  const [selectedRequest, setSelectedRequest] = useState<PermissionRequest | null>(null)
  const [rejectModalVisible, setRejectModalVisible] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  // Query for pending requests only
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['permission-requests', { status: 'pending' }],
    queryFn: () =>
      permissionRequestsApi.list({
        status: 'pending',
        page: 1,
        pageSize: 50,
      }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) => permissionRequestsApi.approve(id),
    onSuccess: () => {
      message.success('Permission request approved successfully')
      queryClient.invalidateQueries({ queryKey: ['permission-requests'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to approve request'))
    },
  })

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      permissionRequestsApi.reject(id, reason),
    onSuccess: () => {
      message.success('Permission request rejected successfully')
      queryClient.invalidateQueries({ queryKey: ['permission-requests'] })
      setRejectModalVisible(false)
      setSelectedRequest(null)
      setRejectReason('')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to reject request'))
    },
  })

  const handleApprove = (request: PermissionRequest) => {
    Modal.confirm({
      title: 'Approve Permission Request',
      content: (
        <div>
          <p>
            <strong>User:</strong> {request.target_user_email || request.target_user_id}
          </p>
          <p>
            <strong>Hospital:</strong> {request.facility_name}
          </p>
          <p>Are you sure you want to approve this request?</p>
        </div>
      ),
      onOk: () => approveMutation.mutate(request.id),
      okText: 'Approve',
      okType: 'primary',
    })
  }

  const handleReject = (request: PermissionRequest) => {
    setSelectedRequest(request)
    setRejectModalVisible(true)
  }

  const handleRejectSubmit = () => {
    if (!selectedRequest) return
    if (!rejectReason.trim()) {
      message.error('Please provide a reason for rejection')
      return
    }
    rejectMutation.mutate({ id: selectedRequest.id, reason: rejectReason })
  }

  const columns: ColumnType<PermissionRequest>[] = [
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      responsive: ['md'],
    },
    {
      title: 'Target User',
      dataIndex: 'target_user_email',
      key: 'target_user_email',
      width: 200,
      render: (email: string | undefined, record: PermissionRequest) => {
        const displayText = email || record.target_user_id
        return (
          <Tooltip title={displayText}>
            <span style={{ cursor: 'help' }}>{displayText}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Hospital',
      dataIndex: 'facility_name',
      key: 'facility_name',
      width: 180,
      render: (name: string) => (
        <Tooltip title={name}>
          <span style={{ cursor: 'help' }}>{name}</span>
        </Tooltip>
      ),
      ellipsis: {
        showTitle: false,
      },
      responsive: ['lg'],
    },
    {
      title: 'Requester',
      dataIndex: 'requester_email',
      key: 'requester_email',
      width: 200,
      render: (email: string | undefined, record: PermissionRequest) => {
        const displayText = email || record.requester_id
        return (
          <Tooltip title={displayText}>
            <span style={{ cursor: 'help' }}>{displayText}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ['md'],
    },
    {
      title: 'Processed By',
      dataIndex: 'approved_by_email',
      key: 'approved_by_email',
      width: 200,
      render: (email: string | null) => {
        if (!email) return <span style={{ color: '#999' }}>—</span>
        return (
          <Tooltip title={email}>
            <span style={{ cursor: 'help' }}>{email}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ['lg'],
    },
    {
      title: 'Expires',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 120,
      render: (date: string) => dayjs(date).fromNow(),
      responsive: ['lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_: unknown, record: PermissionRequest) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="small"
            onClick={() => handleApprove(record)}
            loading={approveMutation.isPending && selectedRequest?.id === record.id}
          >
            Approve
          </Button>
          <Button danger icon={<CloseOutlined />} size="small" onClick={() => handleReject(record)}>
            Reject
          </Button>
        </Space>
      ),
    },
  ]

  const pendingCount = data?.data?.total || 0

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Pending Approval"
                value={pendingCount}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Auto-Refresh"
                value="30s"
                suffix={<ReloadOutlined spin={isLoading} />}
              />
            </Card>
          </Col>
        </Row>

        <Card
          title="Pending Permission Requests"
          extra={
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          }
        >
          <Table
            columns={columns}
            dataSource={data?.data?.requests || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              total: data?.data?.total || 0,
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} requests`,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>

      {/* Reject Modal */}
      <Modal
        title="Reject Permission Request"
        open={rejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setRejectModalVisible(false)
          setSelectedRequest(null)
          setRejectReason('')
        }}
        confirmLoading={rejectMutation.isPending}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        {selectedRequest && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <strong>User:</strong>{' '}
              {selectedRequest.target_user_email || selectedRequest.target_user_id}
            </div>
            <div>
              <strong>Hospital:</strong> {selectedRequest.facility_name}
            </div>
            <div>
              <strong>Reason for Rejection:</strong>
            </div>
            <TextArea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejecting this request..."
            />
          </Space>
        )}
      </Modal>
    </div>
  )
}
